<template>
  <div class="short-text-editor-wrap">
    <main class="page">

      <p class="breadcrumb">
        SEQ / Admin /
        <router-link to="/admin/questions" class="breadcrumb-link">Questions</router-link>
        / {{ breadcrumbTail }}
      </p>

      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">{{ pageTitle }}</h1>
          <p class="page-subtitle">Short text: user types a free-text answer</p>
        </div>
        <div class="page-header-actions">
          <button type="button" class="btn btn-secondary" @click="onCancel">Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!isValid || saving"
            @click="onSave"
          >{{ saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create question') }}</button>
        </div>
      </div>

      <!-- Stub-mode banner: saves don't hit the API yet. -->
      <p v-if="USE_STUB_SAVE" class="stub-banner" role="status">
        <span aria-hidden="true">🧪</span>
        Stub mode — saving builds the payload and shows it below; no backend call is made.
        Flip <code>USE_STUB_SAVE</code> to wire the real API.
      </p>

      <p v-if="serverError" class="server-error" role="alert">{{ serverError }}</p>

      <div v-if="loadError" class="load-error">
        <p>{{ loadError }}</p>
        <router-link to="/admin/questions" class="back-link">← Back</router-link>
      </div>

      <div v-else-if="loading" class="loading-state">Loading question…</div>

      <div v-else class="editor-grid">
        <!-- ───────── LEFT: form ───────── -->
        <section class="form-column">

          <fieldset class="fieldset">
            <legend class="fieldset-legend">Identification</legend>

            <div class="field">
              <label class="field-label" :for="`${uid}-code`">Code</label>
              <input
                :id="`${uid}-code`"
                v-model="form.code"
                type="text"
                class="field-input mono"
                :class="{ 'has-error': errors.code && touched.code }"
                placeholder="POSTCODE"
                autocomplete="off"
                spellcheck="false"
                @blur="touched.code = true"
                @input="onCodeInput"
              >
              <p class="field-hint">
                Short identifier, uppercase letters, digits, and underscores.
                Must be unique within your organization.
              </p>
              <p v-if="errors.code && touched.code" class="field-error">{{ errors.code }}</p>
            </div>

            <div class="field">
              <label class="field-label">Section</label>
              <button
                type="button"
                class="section-pick-btn"
                :class="{ 'is-unassigned': form.section_id == null }"
                @click="showSectionPicker = true"
              >
                <span class="section-pick-label">{{ sectionPickLabel }}</span>
                <span class="section-pick-action">{{ form.section_id == null ? 'Pick section' : 'Change' }}</span>
              </button>
              <button
                v-if="form.section_id != null"
                type="button"
                class="section-clear-btn"
                @click="form.section_id = null"
              >Clear (Unassigned)</button>
            </div>

            <div class="field">
              <label class="field-label">Status</label>
              <div class="radio-row">
                <label class="radio-option">
                  <input type="radio" v-model="form.status" value="active">
                  <span>Active</span>
                </label>
                <label class="radio-option">
                  <input type="radio" v-model="form.status" value="archived">
                  <span>Archived</span>
                </label>
              </div>
            </div>
          </fieldset>

          <!-- Presentation: the light stem. -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">Question content</legend>

            <LocaleSelector
              v-model="activeLang"
              :locales="SUPPORTED_LOCALES"
              :labels="LOCALE_LABELS"
              :filled-fn="isLocaleFilled"
              hint="Fill in any or all languages"
              aria-label="Translation language"
            />

            <div class="field">
              <label class="field-label" :for="`${uid}-title-${activeLang}`">
                Question title <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
              </label>
              <textarea
                :id="`${uid}-title-${activeLang}`"
                v-model="form.content.title[activeLang]"
                class="field-input field-textarea"
                :class="{ 'has-error': errors.title && touched.title }"
                rows="2"
                placeholder="What is your postcode?"
                @blur="touched.title = true"
              ></textarea>
              <p class="field-hint">The main question text. Required in at least one language.</p>
              <p v-if="errors.title && touched.title" class="field-error">{{ errors.title }}</p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-prompt-${activeLang}`">
                Helper line <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <input
                :id="`${uid}-prompt-${activeLang}`"
                v-model="form.content.prompt[activeLang]"
                type="text"
                class="field-input"
                placeholder="We use this only to group answers by region."
              >
              <p class="field-hint">Optional single line shown under the title.</p>
            </div>

            <!-- Optional single stem image, via the shared image library. -->
            <div class="field image-field">
              <label class="field-label">
                Stem image <span class="optional">(optional)</span>
              </label>
              <div class="image-block" :class="{ 'is-empty': form.content.image_id == null }">
                <div class="image-thumb-wrap">
                  <img
                    v-if="pickedImageRecord"
                    class="image-thumb"
                    :src="imageUrl(pickedImageRecord)"
                    :alt="altTextFor(pickedImageRecord, activeLang)"
                    @error="onImgError($event, pickedImageRecord, activeLang)"
                  >
                  <div v-else class="image-placeholder">
                    <span aria-hidden="true">🖼</span>
                  </div>
                </div>
                <div class="image-actions">
                  <button type="button" class="btn btn-small" @click="showImagePicker = true">
                    {{ form.content.image_id == null ? 'Pick image' : 'Change image' }}
                  </button>
                  <button
                    v-if="form.content.image_id != null"
                    type="button"
                    class="btn btn-small btn-ghost"
                    @click="form.content.image_id = null"
                  >Remove</button>
                  <span v-if="form.content.image_id != null" class="image-id mono">#{{ String(form.content.image_id).padStart(4, '0') }}</span>
                </div>
              </div>
              <p v-if="imageLoadError" class="field-hint dim">{{ imageLoadError }}</p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-help-${activeLang}`">
                Help text <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <textarea
                :id="`${uid}-help-${activeLang}`"
                v-model="form.help_text[activeLang]"
                class="field-input field-textarea"
                rows="2"
                placeholder="Shown when the user taps the info button."
              ></textarea>
              <p class="field-hint">Leave empty in all languages to hide the info button.</p>
            </div>
          </fieldset>

          <!-- Input config: language-neutral except the placeholder. -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">Answer input</legend>

            <div class="field">
              <label class="checkbox-option">
                <input type="checkbox" v-model="form.content.input.required">
                <span>Required — the participant must answer before advancing</span>
              </label>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-placeholder-${activeLang}`">
                Placeholder <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <input
                :id="`${uid}-placeholder-${activeLang}`"
                v-model="form.content.input.placeholder[activeLang]"
                type="text"
                class="field-input"
                placeholder="e.g. 8001"
              >
              <p class="field-hint">Grey hint text shown inside the empty field.</p>
            </div>

            <div class="config-row">
              <div class="config-cell">
                <label class="field-label" :for="`${uid}-maxlen`">Max length</label>
                <input
                  :id="`${uid}-maxlen`"
                  v-model="form.content.input.max_length"
                  type="number"
                  min="1"
                  step="1"
                  class="field-input number-input"
                  :class="{ 'has-error': errors.maxLength }"
                  placeholder="none"
                >
              </div>
              <div class="config-cell config-cell-wide">
                <label class="field-label" :for="`${uid}-pattern`">Pattern <span class="optional">(regex, optional)</span></label>
                <input
                  :id="`${uid}-pattern`"
                  v-model="form.content.input.pattern"
                  type="text"
                  class="field-input mono"
                  :class="{ 'has-error': errors.pattern }"
                  placeholder="^[0-9]{4}$"
                  autocomplete="off"
                  spellcheck="false"
                >
              </div>
            </div>
            <p v-if="errors.maxLength" class="field-error">{{ errors.maxLength }}</p>
            <p v-if="errors.pattern" class="field-error">{{ errors.pattern }}</p>
            <p class="field-hint">
              Max length caps the input as the user types. Pattern, if set, must
              match the whole value. Don't know regex? Use a preset below — it
              fills the pattern and a sensible max length for you (both stay
              editable). Validation also runs server-side on submit.
            </p>

            <div class="pattern-presets">
              <span class="pattern-presets-label">Common formats:</span>
              <button
                v-for="p in PATTERN_PRESETS"
                :key="p.label"
                type="button"
                class="preset-chip"
                :class="{ 'is-active': form.content.input.pattern === p.pattern }"
                :title="p.hint"
                @click="applyPreset(p)"
              >{{ p.label }}</button>
              <button
                type="button"
                class="preset-chip preset-chip-clear"
                :disabled="!form.content.input.pattern"
                @click="applyPattern('')"
              >Clear</button>
            </div>
          </fieldset>

          <!-- Stub-mode payload readout: exactly what would POST. -->
          <fieldset v-if="USE_STUB_SAVE && lastPayload" class="fieldset payload-fieldset">
            <legend class="fieldset-legend">Last built payload (stub)</legend>
            <pre class="payload-dump">{{ JSON.stringify(lastPayload, null, 2) }}</pre>
          </fieldset>

        </section>

        <!-- ───────── RIGHT: live preview (the real runtime card) ───────── -->
        <aside class="preview-column">
          <div class="preview-label">Live preview · {{ LOCALE_LABELS[activeLang] }}</div>
          <div class="preview-frame">
            <div class="preview-stage">
              <div class="preview-card">
                <ShortTextCard
                  :key="previewKey"
                  :item="previewItem"
                  :state="previewState"
                  @state-changed="previewState = $event"
                />
              </div>
            </div>
          </div>
          <p class="preview-note">
            The actual runtime card, rendered from this form in the
            <strong>{{ LOCALE_LABELS[activeLang] }}</strong> language. Type in it
            to try the validation; switch tabs to preview another language.
          </p>
        </aside>
      </div>
    </main>

    <!-- Discard-changes confirm -->
    <div
      v-if="showLeaveConfirm"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="leave-title"
      @click.self="onCancelLeave"
    >
      <div class="modal modal-confirm">
        <h2 id="leave-title" class="modal-title">Discard changes?</h2>
        <p class="modal-text">Your unsaved changes will be lost.</p>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="onCancelLeave">Keep editing</button>
          <button type="button" class="btn btn-danger" @click="onConfirmLeave">Discard</button>
        </div>
      </div>
    </div>

    <!-- Section picker -->
    <SectionPickerPopup
      v-if="showSectionPicker"
      :current-section-id="form.section_id"
      @select-section="onSectionPicked"
      @close="showSectionPicker = false"
    />

    <!-- Image picker -->
    <ImagePicker
      v-if="showImagePicker"
      :image-cache="imageCache"
      :initial-image-id="form.content.image_id"
      @picked="onImagePicked"
      @cancel="showImagePicker = false"
    />

    <!-- Toast -->
    <transition name="toast-fade">
      <div
        v-if="toast"
        class="toast"
        :class="`toast-${toast.kind}`"
        role="status"
        aria-live="polite"
      >{{ toast.text }}</div>
    </transition>
  </div>
</template>

<script>
import ShortTextCard from '@/components/quiz/runtime/cards/ShortTextCard.vue';
import LocaleSelector from '@/components/admin/LocaleSelector.vue';
import SectionPickerPopup from '@/components/admin/SectionPickerPopup.vue';
import ImagePicker from '@/components/image_picker/ImagePicker.vue';
import { imageUrl, altTextFor, onImgError } from '@/services/imageHelpers';
import { loadImageMap } from '@/services/imageApi';
import apiService from '@/services/apiService';
import {
  questionsState,
  addQuestion as addStubQuestion,
  blankI18n,
  ensureAllLocales,
  localizeI18n,
  CODE_REGEX,
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  LOCALE_LABELS,
} from '../_data/questions.js';

// The backend now accepts question_type='short_text' (QuestionType literal
// + _validate_short_text_content). Real save is on. Set this back to true to
// demo the editor offline (builds + shows the payload, makes no API call) —
// e.g. before the DB CHECK migration has been applied.
const USE_STUB_SAVE = false;

let uidCounter = 0;

// One-click pattern presets for authors who don't write regex. Clicking a
// chip drops the expression into the Pattern box (still visible + editable)
// and sets a sensible max length — both stay editable afterwards, so the
// fields remain the single source of truth. Kept short on purpose.
const PATTERN_PRESETS = [
  { label: 'Email', pattern: '^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$', maxLength: 254, hint: 'name@example.com' },
  { label: 'Postcode (CH)', pattern: '^[0-9]{4}$', maxLength: 4, hint: 'Four digits, e.g. 8001' },
  { label: 'Digits only', pattern: '^[0-9]+$', maxLength: 10, hint: 'Numbers only' },
  { label: 'Letters only', pattern: "^[A-Za-zÀ-ÿ '-]+$", maxLength: 40, hint: 'Letters, spaces, - and ’ (names, nationality)' },
];

// Trim + drop empty locales (mirrors StepperEditor.trimI18n).
function trimI18n(i18n) {
  const out = {};
  if (i18n && typeof i18n === 'object') {
    for (const loc of SUPPORTED_LOCALES) {
      const v = i18n[loc];
      if (typeof v === 'string' && v.trim()) out[loc] = v.trim();
    }
  }
  return out;
}

function hasAnyLocaleFilled(i18n) {
  if (!i18n || typeof i18n !== 'object') return false;
  return SUPPORTED_LOCALES.some(loc => typeof i18n[loc] === 'string' && i18n[loc].trim().length > 0);
}

export default {
  name: 'ShortTextEditor',
  components: { ShortTextCard, LocaleSelector, SectionPickerPopup, ImagePicker },
  data() {
    uidCounter += 1;
    return {
      USE_STUB_SAVE,
      PATTERN_PRESETS,
      uid: `ste-${uidCounter}`,
      SUPPORTED_LOCALES,
      LOCALE_LABELS,
      activeLang: DEFAULT_LOCALE,
      form: this.buildBlankForm(),
      initialSnapshot: null,
      touched: { code: false, title: false },
      serverError: null,
      loadError: null,
      loading: false,
      saving: false,
      lastPayload: null,
      // Image library cache (Map<id, record>) for the picker + thumbnail.
      imageCache: new Map(),
      imageLoadError: null,
      // Local preview answer state (what the previewed card emits).
      previewState: null,
      showLeaveConfirm: false,
      showSectionPicker: false,
      showImagePicker: false,
      pendingNext: null,
      cancelPending: false,
      toast: null,
      toastTimer: null,
    };
  },
  computed: {
    isEdit() {
      return this.$route.name === 'QuestionEditorEdit';
    },
    questionId() {
      return this.isEdit ? Number(this.$route.params.id) : null;
    },
    sections() {
      return questionsState.sections;
    },
    sectionPickLabel() {
      if (this.form.section_id == null) return 'Unassigned';
      const s = this.sections.find(x => x.id === this.form.section_id);
      if (!s) return `Section ${this.form.section_id}`;
      return localizeI18n(s.name, 'en') || s.slug || `Section ${s.id}`;
    },
    breadcrumbTail() {
      if (this.isEdit) {
        return this.initialSnapshot ? `Edit · ${this.form.code || 'question'}` : 'Edit';
      }
      return 'New short text question';
    },
    pageTitle() {
      return this.isEdit ? 'Edit question' : 'New short text question';
    },
    pickedImageRecord() {
      const id = this.form.content.image_id;
      if (id == null) return null;
      return this.imageCache.get(id) || null;
    },
    errors() {
      const e = {};
      const code = (this.form.code || '').trim();
      if (!code) e.code = 'Code is required.';
      else if (!CODE_REGEX.test(code)) e.code = 'Code must be uppercase letters, digits or underscores. Max 32 chars.';

      if (!hasAnyLocaleFilled(this.form.content.title)) {
        e.title = 'Question title is required in at least one language.';
      }

      const ml = this.form.content.input.max_length;
      if (ml !== '' && ml != null) {
        const n = Number(ml);
        if (!Number.isInteger(n) || n < 1) e.maxLength = 'Max length must be a positive whole number, or empty.';
      }

      const pat = (this.form.content.input.pattern || '').trim();
      if (pat) {
        try { new RegExp(pat); } catch (err) { e.pattern = 'Pattern is not a valid regular expression.'; }
      }
      return e;
    },
    isValid() {
      return Object.keys(this.errors).length === 0;
    },
    isDirty() {
      if (!this.initialSnapshot) return false;
      return JSON.stringify(this.serializeForm()) !== JSON.stringify(this.initialSnapshot);
    },
    // Parsed max length: positive int, or null.
    parsedMaxLength() {
      const n = Number(this.form.content.input.max_length);
      return Number.isInteger(n) && n > 0 ? n : null;
    },
    // Build the localized fields with ONLY the active tab's text, so the
    // previewed card (which resolves against the global app locale with a
    // fallback chain) always shows the language being edited — without
    // mutating the app's global locale or adding a prop to the card.
    previewItem() {
      const only = (field) => {
        const v = (field && field[this.activeLang]) ? field[this.activeLang].trim() : '';
        return v ? { [this.activeLang]: v } : {};
      };
      let sectionName = null;
      if (this.form.section_id != null) {
        const s = this.sections.find(x => x.id === this.form.section_id);
        if (s) sectionName = only(s.name && typeof s.name === 'object' ? s.name : {});
      }
      return {
        id: 'preview',
        item_type: 'question',
        section_id: this.form.section_id,
        section_name: sectionName,
        section_position: null,
        section_count: null,
        item: {
          question_type: 'short_text',
          required: this.form.content.input.required,
          content: {
            title: only(this.form.content.title),
            prompt: only(this.form.content.prompt),
            image_id: this.form.content.image_id,
            input: {
              required: this.form.content.input.required,
              max_length: this.parsedMaxLength,
              placeholder: only(this.form.content.input.placeholder),
              pattern: (this.form.content.input.pattern || '').trim() || null,
            },
          },
        },
      };
    },
    // Remount the previewed card (clearing its typed value) when the
    // structural config changes, so stale answers can't linger.
    previewKey() {
      const i = this.form.content.input;
      return `${this.activeLang}|${i.required}|${this.parsedMaxLength}|${i.pattern}|${this.form.content.image_id}`;
    },
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler(id) {
        if (this.isEdit) this.loadExisting(id);
        else this.resetForNew();
      },
    },
  },
  created() {
    if (!this.isEdit) this.resetForNew();
  },
  mounted() {
    if (!questionsState.sections.length) this.fetchSections();
    this.loadImages();
  },
  methods: {
    buildBlankForm() {
      return {
        code: '',
        section_id: null,
        status: 'active',
        content: {
          title: blankI18n(),
          prompt: blankI18n(),
          image_id: null,
          input: {
            required: false,
            max_length: '',
            placeholder: blankI18n(),
            pattern: '',
          },
        },
        help_text: blankI18n(),
      };
    },

    async fetchSections() {
      // Real read against the existing endpoint (sections aren't gated by
      // the short_text work). Non-fatal: the picker just stays empty.
      try {
        const sections = await apiService.fetchSections();
        questionsState.sections.splice(0, questionsState.sections.length, ...sections);
      } catch (e) {
        console.warn('Could not load sections:', e);
      }
    },

    async loadImages() {
      // Real read against GET /images (same library the content card uses).
      // forceRefresh: pick up images uploaded earlier this session, and
      // refresh the shared cache the preview card reads when it remounts.
      try {
        this.imageCache = await loadImageMap(true);
      } catch (e) {
        this.imageLoadError = 'Image library could not be loaded; picking is unavailable.';
      }
    },

    isLocaleFilled(loc) {
      const c = this.form.content;
      const fields = [
        c.title && c.title[loc],
        c.prompt && c.prompt[loc],
        c.input.placeholder && c.input.placeholder[loc],
        this.form.help_text && this.form.help_text[loc],
      ];
      return fields.some(v => typeof v === 'string' && v.trim().length > 0);
    },

    resetForNew() {
      this.form = this.buildBlankForm();
      this.initialSnapshot = this.serializeForm();
      this.loadError = null;
      this.serverError = null;
      this.lastPayload = null;
      this.previewState = null;
      this.touched = { code: false, title: false };
    },

    async loadExisting(id) {
      // GET /api/seq/questions/:id, scoped to the org. 404 → clear message.
      this.loadError = null;
      this.serverError = null;
      this.loading = true;
      try {
        const q = await apiService.fetchQuestion(id);
        if (q.question_type !== 'short_text') {
          this.loadError = `Question ${id} is a ${q.question_type}, not a short_text question.`;
          this.initialSnapshot = null;
          return;
        }
        const input = (q.content && q.content.input) || {};
        this.form = {
          code: q.code,
          section_id: q.section_id,
          status: q.status,
          content: {
            title: ensureAllLocales(q.content?.title),
            prompt: ensureAllLocales(q.content?.prompt),
            image_id: q.content?.image_id ?? null,
            input: {
              required: input.required === true,
              max_length: input.max_length == null ? '' : String(input.max_length),
              placeholder: ensureAllLocales(input.placeholder),
              pattern: input.pattern || '',
            },
          },
          help_text: ensureAllLocales(q.help_text),
        };
        this.initialSnapshot = this.serializeForm();
        this.touched = { code: false, title: false };
      } catch (e) {
        this.initialSnapshot = null;
        if (e?.response?.status === 404) {
          this.loadError = `No question found with id ${id}.`;
        } else {
          const detail = e?.response?.data?.detail;
          this.loadError = typeof detail === 'string' ? detail : 'Failed to load question from the server.';
        }
      } finally {
        this.loading = false;
      }
    },

    serializeForm() {
      const f = this.form;
      return {
        code: (f.code || '').trim(),
        section_id: f.section_id ?? null,
        status: f.status,
        content: {
          title: trimI18n(f.content.title),
          prompt: trimI18n(f.content.prompt),
          image_id: f.content.image_id ?? null,
          input: {
            required: !!f.content.input.required,
            max_length: this.parsedMaxLength,
            placeholder: trimI18n(f.content.input.placeholder),
            pattern: (f.content.input.pattern || '').trim() || null,
          },
        },
        help_text: trimI18n(f.help_text),
      };
    },

    onCodeInput(e) {
      this.form.code = (e.target.value || '').toUpperCase();
    },

    onImagePicked(id) {
      this.form.content.image_id = id;
      this.showImagePicker = false;
    },

    // Apply a preset: fill the editable Pattern field and set its sensible
    // max length. Both stay editable afterwards.
    applyPreset(preset) {
      this.form.content.input.pattern = preset.pattern;
      this.form.content.input.max_length = preset.maxLength;
    },

    // Clear button: remove the pattern only; leave max length as the author
    // set it.
    applyPattern(pattern) {
      this.form.content.input.pattern = pattern;
    },

    onSectionPicked(section) {
      this.form.section_id = section.id;
      const idx = questionsState.sections.findIndex(s => s.id === section.id);
      if (idx === -1) questionsState.sections.push(section);
      else questionsState.sections.splice(idx, 1, section);
      this.showSectionPicker = false;
    },

    async onSave() {
      this.touched.code = true;
      this.touched.title = true;
      if (!this.isValid || this.saving) return;

      this.serverError = null;
      this.saving = true;
      const f = this.serializeForm();
      const payload = {
        code: f.code,
        section_id: f.section_id,
        question_type: 'short_text',
        content: f.content,
        help_text: f.help_text,
        status: f.status,
      };

      if (USE_STUB_SAVE) {
        // No API call. Show the payload, keep the user on the page, and
        // drop it into the in-session cache so it can be re-opened in edit
        // mode. Clear dirty so leaving doesn't prompt.
        this.lastPayload = payload;
        if (!this.isEdit) {
          try { addStubQuestion(payload); } catch (e) { /* cache is best-effort */ }
        }
        this.initialSnapshot = this.serializeForm();
        this.showToast('success', `(stub) Built ${this.isEdit ? 'update' : 'create'} payload for ${payload.code}. No backend call.`);
        this.saving = false;
        return;
      }

      // ── Real save ── POST /api/seq/questions, or PUT for edit. On update
      // we omit question_type (immutable post-creation; the API ignores it).
      try {
        const saved = this.isEdit
          ? await apiService.updateQuestion(this.questionId, {
            code: payload.code,
            section_id: payload.section_id,
            content: payload.content,
            help_text: payload.help_text,
            status: payload.status,
          })
          : await apiService.createQuestion(payload);
        this.initialSnapshot = this.serializeForm();
        this.showToast('success', this.isEdit ? 'Question saved.' : `Created question ${saved.code}.`);
        this.$router.push('/admin/questions');
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 409) {
          this.serverError = typeof detail === 'string' ? detail : 'A question with that code already exists.';
        } else if (status === 422) {
          this.serverError = Array.isArray(detail)
            ? detail.map(d => d.msg).join(' ')
            : (typeof detail === 'string' ? detail : 'Validation failed.');
        } else {
          this.serverError = typeof detail === 'string' ? detail : 'Could not save question. Please try again.';
        }
      } finally {
        this.saving = false;
      }
    },

    onCancel() {
      if (this.isDirty) {
        this.cancelPending = true;
        this.showLeaveConfirm = true;
        return;
      }
      this.$router.push('/admin/questions');
    },

    onCancelLeave() {
      this.showLeaveConfirm = false;
      if (this.pendingNext) { this.pendingNext(false); this.pendingNext = null; }
      this.cancelPending = false;
    },

    onConfirmLeave() {
      this.showLeaveConfirm = false;
      if (this.cancelPending) {
        this.cancelPending = false;
        this.initialSnapshot = this.serializeForm();
        this.$router.push('/admin/questions');
        return;
      }
      if (this.pendingNext) {
        const next = this.pendingNext;
        this.pendingNext = null;
        this.initialSnapshot = this.serializeForm();
        next();
      }
    },

    showToast(kind, text) {
      if (this.toastTimer) { clearTimeout(this.toastTimer); this.toastTimer = null; }
      this.toast = { kind, text };
      const ms = kind === 'error' ? 6000 : 3500;
      this.toastTimer = setTimeout(() => { this.toast = null; this.toastTimer = null; }, ms);
    },

    imageUrl,
    altTextFor,
    onImgError,
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty && !this.cancelPending) {
      this.pendingNext = next;
      this.showLeaveConfirm = true;
    } else {
      next();
    }
  },
  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
};
</script>

<style scoped>
.short-text-editor-wrap {
  --color-bg-page: #ffffff;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-bad: #C8392E;
  --color-bad-dark: #A92F26;
  --color-bad-soft: rgba(200, 57, 46, 0.10);
  --color-warn-dark: #6B4A0F;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.6;
  min-height: 100%;
  box-sizing: border-box;
}

.short-text-editor-wrap *,
.short-text-editor-wrap *::before,
.short-text-editor-wrap *::after { box-sizing: border-box; }

.page { max-width: 1080px; margin: 0 auto; padding: 2.5rem 1.5rem 4rem; }

.breadcrumb { font-size: 13px; color: var(--color-text-tertiary); margin-bottom: 1rem; letter-spacing: 0.02em; }
.breadcrumb-link { color: var(--color-text-secondary); text-decoration: none; border-bottom: 1px solid transparent; }
.breadcrumb-link:hover { border-bottom-color: var(--color-text-secondary); }

.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 1.25rem; flex-wrap: wrap; }
.page-header-text { flex: 1; min-width: 240px; }
.page-header-actions { display: flex; gap: 8px; flex-shrink: 0; margin-top: 4px; }
.page-title { font-size: 32px; font-weight: 600; margin: 0 0 0.5rem; line-height: 1.2; }
.page-subtitle { font-size: 16px; color: var(--color-text-secondary); margin: 0; font-weight: 400; }

.stub-banner {
  font-size: 13px; color: #3F368F; background: var(--color-accent-soft);
  border: 0.5px solid rgba(83, 74, 183, 0.35); border-radius: var(--radius-md);
  padding: 10px 14px; margin: 0 0 1.25rem; line-height: 1.5;
}
.stub-banner code, .field-hint code, .payload-dump { font-family: var(--font-mono); }
.field-hint code { font-size: 0.92em; background: rgba(0,0,0,0.05); padding: 1px 4px; border-radius: 3px; }

.btn {
  padding: 9px 16px; border-radius: var(--radius-md); font-size: 13px; font-family: inherit;
  font-weight: 500; cursor: pointer; border: 0.5px solid var(--color-border);
  transition: opacity 0.15s, background 0.15s, border-color 0.15s, color 0.15s;
}
.btn:disabled { cursor: not-allowed; opacity: 0.45; }
.btn-secondary { background: var(--color-bg-card); color: var(--color-text-primary); }
.btn-secondary:hover:not(:disabled) { background: var(--color-bg-soft); border-color: var(--color-border-strong); }
.btn-primary { background: var(--color-accent); color: white; border-color: var(--color-accent); }
.btn-primary:hover:not(:disabled) { background: var(--color-accent-dark); border-color: var(--color-accent-dark); }
.btn-danger { background: var(--color-bad); color: white; border-color: var(--color-bad); }
.btn-danger:hover { background: var(--color-bad-dark); border-color: var(--color-bad-dark); }
.btn-small { padding: 6px 12px; font-size: 12px; }
.btn-ghost { background: none; border-color: transparent; color: var(--color-text-tertiary); }
.btn-ghost:hover { color: var(--color-bad); }

.server-error {
  font-size: 13px; color: #6B1F18; background: var(--color-bad-soft);
  border: 0.5px solid rgba(200, 57, 46, 0.45); border-radius: var(--radius-md);
  padding: 10px 14px; margin: 0 0 1.25rem; line-height: 1.5;
}
.load-error { text-align: center; padding: 3rem 1rem; color: var(--color-text-secondary); }
.loading-state { text-align: center; padding: 3rem 1rem; color: var(--color-text-tertiary); font-size: 14px; }
.back-link { color: var(--color-accent); text-decoration: none; font-size: 14px; }
.back-link:hover { text-decoration: underline; }

.editor-grid { display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 32px; align-items: start; }
@media (max-width: 900px) { .editor-grid { grid-template-columns: 1fr; } }

.form-column { display: flex; flex-direction: column; gap: 1.5rem; min-width: 0; }
.preview-column { position: sticky; top: 1.5rem; }
.preview-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--color-text-tertiary); font-weight: 600; margin-bottom: 8px; }
.preview-frame { border: 0.5px solid var(--color-border); border-radius: var(--radius-lg); overflow: hidden; box-shadow: 0 6px 16px rgba(0, 0, 0, 0.04); }
.preview-stage { background: #f5f4ef; padding: 1.5rem; }
.preview-card { background: #fff; border-radius: 12px; padding: 1.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
.preview-note { margin: 10px 0 0; font-size: 11.5px; color: var(--color-text-tertiary); font-style: italic; line-height: 1.4; }

.fieldset { border: 0.5px solid var(--color-border); border-radius: var(--radius-lg); padding: 1.25rem 1.5rem 1.5rem; margin: 0; background: var(--color-bg-card); display: flex; flex-direction: column; gap: 1.25rem; }
.fieldset-legend { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: var(--color-text-tertiary); padding: 0 6px; margin-left: -6px; }

.field { display: flex; flex-direction: column; gap: 6px; }
.field-label { font-size: 13px; font-weight: 500; color: var(--color-text-secondary); display: inline-flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
.lang-tag { font-size: 10px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: var(--color-accent); background: var(--color-accent-soft); padding: 1px 6px; border-radius: 3px; }
.optional { color: var(--color-text-tertiary); font-weight: 400; font-size: 12px; }

.field-input { width: 100%; padding: 10px 12px; border: 0.5px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-card); color: var(--color-text-primary); font-family: var(--font-sans); font-size: 14px; line-height: 1.4; }
.field-input.mono { font-family: var(--font-mono); font-size: 13px; letter-spacing: 0.02em; }
.field-input:focus { outline: none; border-color: var(--color-accent); box-shadow: 0 0 0 2px var(--color-accent-soft); }
.field-input.has-error { border-color: var(--color-bad); box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.15); }
.field-textarea { resize: vertical; min-height: 56px; font-family: var(--font-sans); }
.number-input { font-family: var(--font-mono); font-size: 14px; text-align: center; }

.config-row { display: grid; grid-template-columns: 140px 1fr; gap: 12px; }
.config-cell { display: flex; flex-direction: column; gap: 6px; }
@media (max-width: 520px) { .config-row { grid-template-columns: 1fr; } }

.checkbox-option { display: inline-flex; align-items: center; gap: 8px; font-size: 14px; color: var(--color-text-primary); cursor: pointer; }
.checkbox-option input { accent-color: var(--color-accent); cursor: pointer; }

.pattern-presets { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: -4px; }
.pattern-presets-label { font-size: 12px; color: var(--color-text-tertiary); }
.preset-chip {
  font-size: 12px; padding: 5px 11px; border-radius: 999px; cursor: pointer;
  border: 0.5px solid var(--color-border); background: var(--color-bg-soft);
  color: var(--color-text-secondary); font-family: inherit;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.preset-chip:hover:not(:disabled) { border-color: var(--color-accent); color: var(--color-accent); background: var(--color-accent-soft); }
.preset-chip.is-active { border-color: var(--color-accent); color: var(--color-accent-dark); background: var(--color-accent-soft); font-weight: 600; }
.preset-chip-clear { color: var(--color-text-tertiary); }
.preset-chip:disabled { opacity: 0.4; cursor: not-allowed; }

.image-field .image-block { display: flex; align-items: center; gap: 14px; }
.image-thumb-wrap { width: 72px; height: 72px; flex-shrink: 0; border-radius: var(--radius-md); border: 0.5px solid var(--color-border); overflow: hidden; background: var(--color-bg-soft); display: flex; align-items: center; justify-content: center; }
.image-thumb { width: 100%; height: 100%; object-fit: cover; }
.image-placeholder { font-size: 26px; color: var(--color-text-tertiary); }
.image-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.image-id { font-size: 12px; color: var(--color-text-tertiary); }

.section-pick-btn { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 9px 12px; border: 0.5px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-card); font-family: inherit; font-size: 14px; color: var(--color-text-primary); cursor: pointer; transition: background 0.15s, border-color 0.15s; }
.section-pick-btn:hover { background: var(--color-bg-soft); border-color: var(--color-border-strong); }
.section-pick-btn.is-unassigned .section-pick-label { color: var(--color-text-tertiary); font-style: italic; }
.section-pick-action { font-size: 12px; font-weight: 600; color: var(--color-accent); letter-spacing: 0.02em; flex-shrink: 0; }
.section-clear-btn { align-self: flex-start; margin-top: 4px; background: none; border: none; color: var(--color-text-tertiary); font-family: inherit; font-size: 12px; cursor: pointer; padding: 2px 0; text-decoration: underline; }
.section-clear-btn:hover { color: var(--color-text-secondary); }

.field-hint { font-size: 11.5px; color: var(--color-text-tertiary); margin: 0; line-height: 1.4; }
.field-hint.dim { font-style: italic; color: var(--color-warn-dark); }
.field-error { font-size: 12px; color: var(--color-bad); margin: 0; }

.radio-row { display: flex; gap: 18px; padding: 4px 0; }
.radio-option { display: inline-flex; align-items: center; gap: 6px; font-size: 14px; color: var(--color-text-primary); cursor: pointer; }
.radio-option input { accent-color: var(--color-accent); cursor: pointer; }

.payload-fieldset { background: var(--color-bg-soft); }
.payload-dump { margin: 0; font-size: 12px; line-height: 1.45; color: var(--color-text-primary); background: rgba(0,0,0,0.04); border: 0.5px solid var(--color-border); border-radius: var(--radius-md); padding: 12px 14px; overflow-x: auto; white-space: pre; }

.modal-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.4); display: flex; align-items: center; justify-content: center; padding: 1.25rem; z-index: 2000; }
.modal { background: var(--color-bg-card); border-radius: var(--radius-lg); border: 0.5px solid var(--color-border); padding: 1.5rem; max-width: 420px; width: 100%; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15); }
.modal-title { font-size: 18px; font-weight: 600; margin: 0 0 8px; }
.modal-text { font-size: 14px; color: var(--color-text-secondary); margin: 0 0 1.25rem; line-height: 1.5; }
.modal-footer { display: flex; justify-content: space-between; gap: 10px; }

.toast { position: fixed; left: 50%; bottom: 28px; transform: translateX(-50%); background: var(--color-text-primary); color: white; padding: 11px 18px; border-radius: var(--radius-md); font-size: 13px; line-height: 1.4; box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18); max-width: min(90vw, 520px); z-index: 3000; }
.toast-success { background: #2E7D32; }
.toast-error { background: var(--color-bad-dark); }
.toast-fade-enter-active, .toast-fade-leave-active { transition: opacity 0.2s, transform 0.2s; }
.toast-fade-enter-from, .toast-fade-leave-to { opacity: 0; transform: translate(-50%, 8px); }
</style>
